// Centralized date filtering logic for consistent behavior across the application
// All date ranges are dynamically calculated based on current date

export interface DateRange {
  from: Date;
  to: Date;
}

export interface DateRangeString {
  startDate: string;
  endDate: string;
}

/**
 * Default timezone for business operations (Eastern Time)
 */
export const DEFAULT_TIMEZONE = 'America/New_York';


/**
 * Get today's date as a YYYY-MM-DD string in a specific timezone
 * @param timezone - IANA timezone (defaults to America/New_York)
 * @returns YYYY-MM-DD string
 */
export function getTodayString(timezone: string = DEFAULT_TIMEZONE): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());
}

/**
 * Get yesterday's date as a YYYY-MM-DD string in a specific timezone
 * TIMEZONE-SAFE: Uses noon-anchored date calculation to avoid DST issues
 * @param timezone - IANA timezone (defaults to America/New_York)
 * @returns YYYY-MM-DD string
 */
export function getYesterdayString(timezone: string = DEFAULT_TIMEZONE): string {
  const today = getTodayString(timezone);
  // Parse today at noon and subtract one day
  const date = new Date(today + 'T12:00:00');
  date.setDate(date.getDate() - 1);
  // Extract YYYY-MM-DD directly from the noon-anchored date
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get today's date in a specific timezone as a Date object
 * CRITICAL: Creates a Date representing midnight UTC of the timezone's date
 * This ensures timezone-independent date calculations
 * @param timezone - IANA timezone (defaults to America/New_York)
 */
export function getTodayInTimezone(timezone: string = DEFAULT_TIMEZONE): Date {
  const dateStr = getTodayString(timezone);
  // Parse as UTC noon to avoid timezone boundary issues
  // Using noon ensures the date stays correct when formatted in any US timezone
  return new Date(dateStr + 'T12:00:00Z');
}

/**
 * Get yesterday's date in a specific timezone as a Date object
 * @param timezone - IANA timezone (defaults to America/New_York)
 */
export function getYesterdayInTimezone(timezone: string = DEFAULT_TIMEZONE): Date {
  const dateStr = getYesterdayString(timezone);
  // Parse as UTC noon to avoid timezone boundary issues
  return new Date(dateStr + 'T12:00:00Z');
}

/**
 * Format a Date object as YYYY-MM-DD in a specific timezone
 * @param date - The Date object to format
 * @param timezone - IANA timezone (defaults to America/New_York)
 * @returns YYYY-MM-DD string
 */
export function formatDateInTimezone(date: Date, timezone: string = DEFAULT_TIMEZONE): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
}

// Legacy alias for backward compatibility
export const getTodayInEasternTime = () => getTodayInTimezone(DEFAULT_TIMEZONE);

/**
 * Get standardized date range based on preset
 * All presets use the same logic across dashboard, analytics, and other components
 * IMPORTANT: Uses specified timezone for today/yesterday to match backend behavior
 * @param preset - Date filter preset name
 * @param timezone - IANA timezone (defaults to America/New_York)
 */
export function getDateRangeFromPreset(
  preset: string,
  timezone: string = DEFAULT_TIMEZONE,
): DateRange {
  const now = new Date();
  const today = getTodayInTimezone(timezone);
  
  switch (preset) {
    case 'today':
      // Current day in specified timezone
      return {
        from: new Date(today),
        to: new Date(today)
      };
    
    case 'yesterday':
      // Previous day in specified timezone
      const yesterday = getYesterdayInTimezone(timezone);
      return {
        from: new Date(yesterday),
        to: new Date(yesterday)
      };
    
    case 'thisMonth':
      // Current month from 1st to today
      return {
        from: new Date(now.getFullYear(), now.getMonth(), 1),
        to: new Date(today)
      };
    
    case 'lastMonth':
      // Previous complete month
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      return {
        from: new Date(lastMonth),
        to: new Date(lastMonthEnd)
      };
    
    case 'thisWeek':
      // Current week (Monday to Sunday)
      const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const daysFromMonday = (dayOfWeek + 6) % 7; // Convert to Monday-based week
      const thisWeekStart = new Date(today);
      thisWeekStart.setDate(today.getDate() - daysFromMonday);
      const thisWeekEnd = new Date(thisWeekStart);
      thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
      return {
        from: new Date(thisWeekStart),
        to: new Date(Math.min(thisWeekEnd.getTime(), today.getTime())) // Don't go beyond today
      };
    
    case 'lastWeek':
      // Previous complete week (Monday to Sunday)
      const currentDayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const daysFromLastMonday = (currentDayOfWeek + 6) % 7; // Convert to Monday-based week
      const lastWeekStart = new Date(today);
      lastWeekStart.setDate(today.getDate() - daysFromLastMonday - 7);
      const lastWeekEnd = new Date(lastWeekStart);
      lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
      return {
        from: new Date(lastWeekStart),
        to: new Date(lastWeekEnd)
      };
    
    case 'last30Days':
    case '30':
      // Last 30 days from today
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return {
        from: new Date(thirtyDaysAgo),
        to: new Date(today)
      };
    
    case 'last60Days':
    case '60':
      // Last 60 days from today
      const sixtyDaysAgo = new Date(today);
      sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
      return {
        from: new Date(sixtyDaysAgo),
        to: new Date(today)
      };
    
    case 'last90Days':
    case 'last3Months':
    case '90':
      // Last 90 days from today
      const ninetyDaysAgo = new Date(today);
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      return {
        from: new Date(ninetyDaysAgo),
        to: new Date(today)
      };
    
    case 'last180Days':
    case '180':
      // Last 180 days from today
      const oneEightyDaysAgo = new Date(today);
      oneEightyDaysAgo.setDate(oneEightyDaysAgo.getDate() - 180);
      return {
        from: new Date(oneEightyDaysAgo),
        to: new Date(today)
      };
    
    case 'thisYear':
      // Current year from January 1st to today
      return {
        from: new Date(now.getFullYear(), 0, 1),
        to: new Date(today)
      };
    
    case 'lastYear':
      // Previous year complete
      return {
        from: new Date(now.getFullYear() - 1, 0, 1),
        to: new Date(now.getFullYear() - 1, 11, 31)
      };
    
    case 'last365Days':
    case '365':
      // Last 365 days from today
      const oneYearAgo = new Date(today);
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      return {
        from: new Date(oneYearAgo),
        to: new Date(today)
      };
    
    case 'all':
    case 'allTime':
    default:
      // All time data - use generous range
      return {
        from: new Date(2024, 0, 1),
        to: new Date(today)
      };
  }
}

/**
 * Convert DateRange to string format for API calls
 * Uses timezone-safe formatting to avoid date shifts
 * @param dateRange - The DateRange to convert
 * @param timezone - IANA timezone (defaults to America/New_York)
 */
export function dateRangeToStrings(dateRange: DateRange, timezone: string = DEFAULT_TIMEZONE): DateRangeString {
  return {
    startDate: formatDateInTimezone(dateRange.from, timezone),
    endDate: formatDateInTimezone(dateRange.to, timezone)
  };
}

/**
 * Convert DateRange to ISO string format for API calls (with time)
 */
export function dateRangeToISOStrings(dateRange: DateRange): DateRangeString {
  return {
    startDate: dateRange.from.toISOString(),
    endDate: dateRange.to.toISOString()
  };
}

/**
 * Validate if a date range is within available data bounds
 * @param dateRange - The DateRange to validate
 * @param timezone - IANA timezone (defaults to America/New_York)
 */
export function isValidDateRange(dateRange: DateRange, timezone: string = DEFAULT_TIMEZONE): boolean {
  const today = getTodayInTimezone(timezone);
  const minDate = new Date(2024, 0, 1); // Allow data from 2024 onwards

  return dateRange.from >= minDate &&
         dateRange.to <= today &&
         dateRange.from <= dateRange.to;
}

/**
 * Clamp date range to available data bounds
 * @param dateRange - The DateRange to clamp
 * @param timezone - IANA timezone (defaults to America/New_York)
 */
export function clampDateRange(dateRange: DateRange, timezone: string = DEFAULT_TIMEZONE): DateRange {
  const today = getTodayInTimezone(timezone);
  const minDate = new Date(2024, 0, 1); // Allow data from 2024 onwards

  return {
    from: dateRange.from < minDate ? minDate : dateRange.from,
    to: dateRange.to > today ? today : dateRange.to
  };
}

/**
 * Returns true when the given range covers exactly one calendar month: the
 * start date is the 1st and the end date is that month's last day (handles
 * leap years automatically). Used to pick between `/ month` and `/ period`
 * wording for flat-fee labels without changing any underlying math.
 *
 * Accepts either `Date` objects or `YYYY-MM-DD` strings (the format stored on
 * royalty invoices and used by the Royalties page's `startDate` / `endDate`).
 */
export function isCalendarMonthRange(
  start: Date | string,
  end: Date | string,
): boolean {
  const s = typeof start === "string" ? new Date(`${start}T00:00:00`) : start;
  const e = typeof end === "string" ? new Date(`${end}T00:00:00`) : end;
  if (!(s instanceof Date) || !(e instanceof Date)) return false;
  if (!Number.isFinite(s.getTime()) || !Number.isFinite(e.getTime())) return false;
  if (s.getDate() !== 1) return false;
  if (s.getFullYear() !== e.getFullYear() || s.getMonth() !== e.getMonth()) return false;
  const lastDay = new Date(e.getFullYear(), e.getMonth() + 1, 0).getDate();
  return e.getDate() === lastDay;
}

/**
 * Standard date preset options for dropdowns
 */
export const DATE_PRESETS = [
  { value: 'thisWeek', label: 'This Week' },
  { value: 'lastWeek', label: 'Last Week' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'last30Days', label: 'Last 30 Days' },
  { value: 'last90Days', label: 'Last 90 Days' },
  { value: 'last3Months', label: 'Last 3 Months' },
  { value: 'custom', label: 'Custom Range' }
];

/**
 * Store performance date preset options (using numeric values for backward compatibility)
 */
export const STORE_PERFORMANCE_PRESETS = [
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 90 Days' },
  { value: '180', label: 'Last 6 Months' },
  { value: '365', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' }
];
